/**
 * PaletteRestrictor - Limits visible blocks to scaffold learning.
 *
 * Hides blocks not in the allowed list to reduce cognitive load
 * for young children. Blocks are shown/hidden via CSS classes,
 * not removed from the DOM (preserving ScratchJr's internal state).
 *
 * @see GitHub Issue #852
 */

import BlockSpecs from '../../../blocks/BlockSpecs';
import Palette from '../../../ui/Palette';

export default class PaletteRestrictor {
    /**
     * @param {HTMLElement} container - Editor container
     * @param {string[]} allowedBlocks - Block types to show (e.g., ['forward', 'back', 'hop', 'repeat'])
     */
    constructor (container, allowedBlocks) {
        this._container = container;
        this._allowedBlocks = new Set(allowedBlocks || []);
        this._hiddenElements = [];
        this._applied = false;
        this._paletteObserver = null;
    }

    /**
     * Apply palette restriction — hide non-allowed blocks.
     */
    apply () {
        if (this._applied) return;
        this._applied = true;

        this._applyCategoryVisibility();
        this._ensureValidCategorySelection();
        this._applyPaletteBlockVisibility();
        this._observePalette();
    }

    /**
     * Remove all restrictions — show all blocks.
     */
    remove () {
        if (this._paletteObserver) {
            this._paletteObserver.disconnect();
            this._paletteObserver = null;
        }
        for (const el of this._hiddenElements) {
            el.classList.remove('activity-block-hidden', 'activity-category-hidden');
            el.style.removeProperty('display');
            el.style.removeProperty('pointer-events');
            el.style.removeProperty('opacity');
        }
        this._hiddenElements = [];
        this._applied = false;
    }

    /**
     * Update allowed blocks list.
     * @param {string[]} newAllowed
     */
    updateAllowed (newAllowed) {
        this.remove();
        this._allowedBlocks = new Set(newAllowed || []);
        this.apply();
    }

    destroy () {
        this.remove();
        this._container = null;
    }

    _observePalette () {
        const palette = this._getRoot().querySelector('#palette');
        if (!palette || typeof MutationObserver === 'undefined') {
            return;
        }

        this._paletteObserver = new MutationObserver(() => {
            if (!this._applied) {
                return;
            }
            this._applyPaletteBlockVisibility();
        });

        this._paletteObserver.observe(palette, {childList: true});
    }

    _applyPaletteBlockVisibility () {
        const palette = this._getRoot().querySelector('#palette');
        if (!palette) {
            return;
        }

        for (const block of Array.from(palette.childNodes || [])) {
            if (!block || !block.owner || !block.owner.blocktype) {
                continue;
            }

            if (this._allowedBlocks.has(block.owner.blocktype)) {
                block.classList.remove('activity-block-hidden');
                block.style.removeProperty('display');
            } else {
                block.classList.add('activity-block-hidden');
                block.style.display = 'none';
                this._hiddenElements.push(block);
            }
        }
    }

    _applyCategoryVisibility () {
        const selectors = this._getRoot().querySelector('#selectors');
        if (!selectors) {
            return;
        }

        const allowedCategories = this._getAllowedCategories();
        for (const selector of Array.from(selectors.childNodes || [])) {
            if (!selector || typeof selector.index !== 'number') {
                continue;
            }

            if (allowedCategories.has(selector.index)) {
                selector.classList.remove('activity-category-hidden');
                selector.style.removeProperty('display');
                selector.style.removeProperty('pointer-events');
                selector.style.removeProperty('opacity');
            } else {
                selector.classList.add('activity-category-hidden');
                selector.style.display = 'none';
                selector.style.pointerEvents = 'none';
                selector.style.opacity = '0';
                this._hiddenElements.push(selector);
            }
        }
    }

    _ensureValidCategorySelection () {
        const allowedCategories = this._getAllowedCategories();
        if (allowedCategories.size === 0) {
            return;
        }

        if (allowedCategories.has(Palette.numcat)) {
            return;
        }

        const [firstAllowed] = Array.from(allowedCategories.values()).sort((a, b) => a - b);
        if (typeof firstAllowed === 'number') {
            Palette.selectCategory(firstAllowed);
        }
    }

    _getAllowedCategories () {
        const categories = new Set();

        (BlockSpecs.palettes || []).forEach((categoryBlocks, index) => {
            const matches = (categoryBlocks || []).some((blockType) => (
                blockType !== 'space' && this._allowedBlocks.has(blockType)
            ));
            if (matches) {
                categories.add(index);
            }
        });

        return categories;
    }

    _getRoot () {
        return (this._container && this._container.ownerDocument) || document;
    }
}
